import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Clock, Check, X, AlertTriangle, ChevronDown,
  Shield, Send, Upload, Globe, Eye, FileText, User,
  Zap, ArrowUpRight, ArrowDownLeft, Building2, Loader2, Filter,
  CheckCircle2, XCircle, AlertCircle, Info, RefreshCw, Receipt
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ============================================================================
// TYPES
// ============================================================================
export type ActivityType =
  | 'trade_created'
  | 'trade_submitted'
  | 'compliance_check'
  | 'trade_approved'
  | 'trade_rejected'
  | 'trade_sent'
  | 'trade_executed'
  | 'trade_posted'
  | 'trade_failed'
  | 'position_update'
  | 'settlement_update'
  | 'corporate_action'
  | 'user_action'
  | 'system_event'
  | 'fix_message';

export type ActivityStatus = 'pending' | 'in_progress' | 'success' | 'warning' | 'error';

export interface ActivityEvidence {
  type: 'metric' | 'rule' | 'quote' | 'fix' | 'document';
  label: string;
  value: string | number;
  unit?: string;
  status?: 'positive' | 'negative' | 'neutral';
}

export interface ActivityStep {
  id: string;
  stage: string;
  action: string;
  status: ActivityStatus;
  timestamp: Date;
  duration?: number;
  user?: string;
  evidence?: ActivityEvidence[];
}

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  description?: string;
  status: ActivityStatus;
  timestamp: Date;
  tradeId?: string;
  orderId?: string;
  portfolio?: string;
  security?: string;
  side?: 'BUY' | 'SELL';
  quantity?: number;
  price?: number;
  user?: string;
  steps?: ActivityStep[];
  evidence?: ActivityEvidence[];
  metadata?: Record<string, any>;
  expanded?: boolean;
}

interface ActivityStreamProps {
  activities: ActivityItem[];
  onActivityClick?: (activity: ActivityItem) => void;
  onApprove?: (activity: ActivityItem) => void;
  onReject?: (activity: ActivityItem) => void;
  onViewDetails?: (activity: ActivityItem) => void;
  isLoading?: boolean;
  autoScroll?: boolean;
  maxHeight?: string;
  showFilters?: boolean;
}

// ============================================================================
// ICONS & COLORS
// ============================================================================
const activityIcons: Record<ActivityType, React.ReactNode> = {
  trade_created: <ArrowUpRight className="w-4 h-4" />,
  trade_submitted: <Send className="w-4 h-4" />,
  compliance_check: <Shield className="w-4 h-4" />,
  trade_approved: <CheckCircle2 className="w-4 h-4" />,
  trade_rejected: <XCircle className="w-4 h-4" />,
  trade_sent: <Globe className="w-4 h-4" />,
  trade_executed: <Zap className="w-4 h-4" />,
  trade_posted: <Upload className="w-4 h-4" />,
  trade_failed: <XCircle className="w-4 h-4" />,
  position_update: <RefreshCw className="w-4 h-4" />,
  settlement_update: <Clock className="w-4 h-4" />,
  corporate_action: <Receipt className="w-4 h-4" />,
  user_action: <User className="w-4 h-4" />,
  system_event: <Info className="w-4 h-4" />,
  fix_message: <Activity className="w-4 h-4" />,
};

const statusColors: Record<ActivityStatus, { bg: string; text: string; border: string; icon: string }> = {
  pending: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20', icon: 'text-slate-400' },
  in_progress: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: 'text-blue-400' },
  success: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: 'text-emerald-400' },
  warning: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: 'text-amber-400' },
  error: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: 'text-red-400' },
};

const workflowStageColors: Record<string, string> = {
  DRAFT: 'text-slate-400',
  PENDING_PRECLEAR: 'text-amber-400',
  APPROVED: 'text-emerald-400',
  SENT: 'text-blue-400',
  EXECUTED: 'text-violet-400',
  POSTED: 'text-green-400',
  REJECTED: 'text-red-400',
  FAILED: 'text-red-400',
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
const StatusIndicator: React.FC<{ status: ActivityStatus; size?: 'sm' | 'md' }> = ({ status, size = 'md' }) => {
  const sizeClass = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';
  const colors = statusColors[status];

  if (status === 'in_progress') {
    return (
      <span className="relative flex">
        <span className={cn("animate-ping absolute inline-flex rounded-full opacity-75", sizeClass, "bg-blue-400")} />
        <span className={cn("relative inline-flex rounded-full", sizeClass, "bg-blue-400")} />
      </span>
    );
  }

  return <span className={cn("inline-flex rounded-full", sizeClass, colors.bg.replace('/10', ''))} />;
};

const EvidenceItem: React.FC<{ evidence: ActivityEvidence }> = ({ evidence }) => {
  const statusColors = {
    positive: 'text-emerald-400',
    negative: 'text-red-400',
    neutral: 'text-slate-400',
  };

  return (
    <div className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded text-xs">
      <span className="text-slate-400">{evidence.label}</span>
      <span className={cn(
        "font-medium",
        evidence.status ? statusColors[evidence.status] : 'text-white'
      )}>
        {typeof evidence.value === 'number' && evidence.type === 'metric'
          ? evidence.value.toFixed(2)
          : evidence.value}
        {evidence.unit && <span className="text-slate-500 ml-1">{evidence.unit}</span>}
      </span>
    </div>
  );
};

const TradeBadge: React.FC<{ side: 'BUY' | 'SELL'; security?: string }> = ({ side, security }) => (
  <span className={cn(
    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
    "bg-slate-800 border border-white/10",
    side === 'BUY' ? 'text-emerald-400' : 'text-red-400'
  )}>
    {side === 'BUY' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
    {side} {security && <span className="text-slate-400">{security}</span>}
  </span>
);

const WorkflowStageBadge: React.FC<{ stage: string }> = ({ stage }) => (
  <span className={cn(
    "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
    "bg-slate-800/80 border border-white/10",
    workflowStageColors[stage] || 'text-slate-400'
  )}>
    {stage.replace('_', ' ')}
  </span>
);

const TimeAgo: React.FC<{ timestamp: Date }> = ({ timestamp }) => {
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const diff = now.getTime() - timestamp.getTime();
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);

      if (seconds < 60) setTimeAgo('just now');
      else if (minutes < 60) setTimeAgo(`${minutes}m ago`);
      else if (hours < 24) setTimeAgo(`${hours}h ago`);
      else setTimeAgo(timestamp.toLocaleDateString());
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [timestamp]);

  return <span className="text-xs text-slate-500">{timeAgo}</span>;
};

// ============================================================================
// ACTIVITY CARD
// ============================================================================
const ActivityCard: React.FC<{
  activity: ActivityItem;
  onClick?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onViewDetails?: () => void;
}> = ({ activity, onClick, onApprove, onReject, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(activity.expanded ?? false);
  const colors = statusColors[activity.status];

  const hasSteps = activity.steps && activity.steps.length > 0;
  const hasEvidence = activity.evidence && activity.evidence.length > 0;
  const needsAction = activity.type === 'trade_submitted' && activity.status === 'pending';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        "relative bg-slate-900 border rounded-xl overflow-hidden transition-all",
        colors.border,
        onClick && "cursor-pointer hover:border-white/20"
      )}
    >
      {/* Main Content */}
      <div
        className="p-4"
        onClick={() => {
          if (hasSteps || hasEvidence) setIsExpanded(!isExpanded);
          onClick?.();
        }}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={cn("p-2 rounded-lg", colors.bg)}>
            <span className={colors.icon}>
              {activityIcons[activity.type]}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {activity.side && <TradeBadge side={activity.side} security={activity.security} />}
              {activity.metadata?.stage && <WorkflowStageBadge stage={activity.metadata.stage} />}
              <TimeAgo timestamp={activity.timestamp} />
              {activity.status === 'in_progress' && (
                <span className="flex items-center gap-1 text-xs text-blue-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Processing
                </span>
              )}
            </div>

            <h4 className="font-medium text-white text-sm">{activity.title}</h4>

            {activity.description && (
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{activity.description}</p>
            )}

            {/* Trade Details */}
            {activity.quantity && (
              <div className="flex items-center gap-3 mt-2 text-xs">
                <span className="text-slate-500">
                  Qty: <span className="text-white">${(activity.quantity / 1000000).toFixed(2)}M</span>
                </span>
                {activity.price && (
                  <span className="text-slate-500">
                    Price: <span className="text-white">{activity.price.toFixed(3)}</span>
                  </span>
                )}
                {activity.portfolio && (
                  <span className="text-slate-500">
                    Portfolio: <span className="text-white">{activity.portfolio}</span>
                  </span>
                )}
              </div>
            )}

            {/* Quick Evidence Preview */}
            {hasEvidence && !isExpanded && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {activity.evidence!.slice(0, 3).map((ev, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-slate-800 rounded text-slate-400">
                    {ev.label}: <span className="text-white">{ev.value}{ev.unit}</span>
                  </span>
                ))}
                {activity.evidence!.length > 3 && (
                  <span className="text-xs text-slate-500">+{activity.evidence!.length - 3} more</span>
                )}
              </div>
            )}
          </div>

          {/* Expand/Actions */}
          <div className="flex items-center gap-2">
            {needsAction && (
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
                  className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                  title="Approve"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onReject?.(); }}
                  className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                  title="Reject"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {(hasSteps || hasEvidence) && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 transition-colors"
              >
                <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}>
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-white/10"
          >
            <div className="p-4 space-y-4">
              {/* Workflow Steps Timeline */}
              {hasSteps && (
                <div>
                  <h5 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                    Workflow History
                  </h5>
                  <div className="relative pl-6">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-slate-700" />

                    {activity.steps!.map((step, index) => (
                      <div key={step.id} className="relative pb-4 last:pb-0">
                        <div className={cn(
                          "absolute -left-4 w-4 h-4 rounded-full border-2 flex items-center justify-center",
                          step.status === 'success' && "border-emerald-500 bg-emerald-500/20",
                          step.status === 'error' && "border-red-500 bg-red-500/20",
                          step.status === 'in_progress' && "border-blue-500 bg-blue-500/20",
                          step.status === 'pending' && "border-slate-600 bg-slate-800",
                          step.status === 'warning' && "border-amber-500 bg-amber-500/20"
                        )}>
                          {step.status === 'in_progress' && (
                            <Loader2 className="w-2 h-2 text-blue-400 animate-spin" />
                          )}
                          {step.status === 'success' && (
                            <Check className="w-2 h-2 text-emerald-400" />
                          )}
                          {step.status === 'error' && (
                            <X className="w-2 h-2 text-red-400" />
                          )}
                        </div>

                        <div className="ml-2">
                          <div className="flex items-center gap-2">
                            <span className={cn("text-xs font-medium", workflowStageColors[step.stage] || 'text-slate-400')}>
                              {step.stage}
                            </span>
                            {step.user && (
                              <span className="text-xs text-slate-500">by {step.user}</span>
                            )}
                            {step.duration && (
                              <span className="text-xs text-slate-600">
                                {step.duration < 1000 ? `${step.duration}ms` : `${(step.duration / 1000).toFixed(1)}s`}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-300 mt-0.5">{step.action}</p>

                          {step.evidence && step.evidence.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {step.evidence.map((ev, i) => (
                                <EvidenceItem key={i} evidence={ev} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Evidence */}
              {hasEvidence && (
                <div>
                  <h5 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                    Details
                  </h5>
                  <div className="grid grid-cols-2 gap-2">
                    {activity.evidence!.map((ev, i) => (
                      <EvidenceItem key={i} evidence={ev} />
                    ))}
                  </div>
                </div>
              )}

              {/* View Details Button */}
              {onViewDetails && (
                <button
                  onClick={onViewDetails}
                  className="w-full py-2 text-sm text-blue-400 hover:text-blue-300 flex items-center justify-center gap-2 border border-blue-500/20 rounded-lg hover:bg-blue-500/10 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View Full Details
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================================================
// ACTIVITY STREAM COMPONENT
// ============================================================================
export const ActivityStream: React.FC<ActivityStreamProps> = ({
  activities,
  onActivityClick,
  onApprove,
  onReject,
  onViewDetails,
  isLoading = false,
  autoScroll = true,
  maxHeight = '600px',
  showFilters = true,
}) => {
  const [filter, setFilter] = useState<ActivityType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ActivityStatus | 'all'>('all');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [activities.length, autoScroll]);

  const filteredActivities = activities.filter(a => {
    if (filter !== 'all' && a.type !== filter) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    return true;
  });

  const pendingCount = activities.filter(a => a.status === 'pending' || a.status === 'in_progress').length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-white">Trade Activity</h3>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-300 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>

        {showFilters && (
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ActivityStatus | 'all')}
              className="px-2 py-1 text-xs bg-slate-800 border border-white/10 rounded text-white outline-none"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
        )}
      </div>

      {/* Activity List */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{ maxHeight }}
      >
        {isLoading && activities.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No trade activity yet</p>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {filteredActivities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                onClick={() => onActivityClick?.(activity)}
                onApprove={() => onApprove?.(activity)}
                onReject={() => onReject?.(activity)}
                onViewDetails={() => onViewDetails?.(activity)}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default ActivityStream;
